import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, m } from '../../lib/motion.js';
import { cn } from '../../lib/format.js';
import { useReducedMotion } from '../../hooks/useReducedMotion.js';

/**
 * A slide-over panel and a centred dialog, sharing one implementation of the
 * things that are easy to get wrong: focus is trapped while open, Escape closes,
 * the page behind does not scroll, and focus returns to whatever opened it.
 *
 * Motion collapses to a plain fade when the visitor has asked for reduced
 * motion, rather than being removed — the transition still communicates that
 * something arrived.
 */

/**
 * Portals need a live document. The storefront ships as an SPA so this is always
 * true in the browser, but guarding it keeps these components renderable outside
 * one — which is what makes them testable, and what would make server rendering
 * possible later without unpicking every overlay.
 */
const portalTarget = () => (typeof document === 'undefined' ? null : document.body);

const useDismissable = (open, onClose) => {
  const panelRef = useRef(null);
  const returnFocusRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    returnFocusRef.current = document.activeElement;

    const { overflow, paddingRight } = document.body.style;
    // Compensating for the scrollbar stops the page shifting sideways as it locks.
    const gap = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = 'hidden';
    if (gap > 0) document.body.style.paddingRight = `${gap}px`;

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
        return;
      }

      if (event.key !== 'Tab' || !panelRef.current) return;

      const focusable = panelRef.current.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown, true);

    // Focus the panel itself rather than its first control, so a screen reader
    // reads the heading before the first button.
    const timer = setTimeout(() => panelRef.current?.focus(), 30);

    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      clearTimeout(timer);
      document.body.style.overflow = overflow;
      document.body.style.paddingRight = paddingRight;
      returnFocusRef.current?.focus?.();
    };
  }, [open, onClose]);

  return panelRef;
};

const sides = {
  right: { class: 'inset-y-0 right-0 h-full w-full max-w-md border-l', axis: 'x', from: '100%' },
  left: { class: 'inset-y-0 left-0 h-full w-full max-w-md border-r', axis: 'x', from: '-100%' },
  bottom: { class: 'inset-x-0 bottom-0 max-h-[85vh] w-full border-t', axis: 'y', from: '100%' },
};

export const Sheet = ({ open, onClose, side = 'right', title, description, children, footer, className }) => {
  const panelRef = useDismissable(open, onClose);
  const reduced = useReducedMotion();
  const config = sides[side];
  const target = portalTarget();

  if (!target) return null;

  return createPortal(
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-50" role="presentation">
          <m.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            className="absolute inset-0 bg-ink/30 backdrop-blur-[2px]"
          />

          <m.div
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-label={title}
            tabIndex={-1}
            initial={reduced ? { opacity: 0 } : { [config.axis]: config.from }}
            animate={reduced ? { opacity: 1 } : { [config.axis]: 0 }}
            exit={reduced ? { opacity: 0 } : { [config.axis]: config.from }}
            transition={{ duration: reduced ? 0.15 : 0.32, ease: [0.25, 1, 0.5, 1] }}
            className={cn(
              'absolute flex flex-col border-mist bg-surface shadow-[0_0_60px_-15px_rgba(20,23,26,0.25)] outline-none',
              config.class,
              className,
            )}
          >
            {title && (
              <header className="flex items-start justify-between gap-4 border-b border-mist px-5 py-4">
                <div>
                  <h2 className="font-display text-lg tracking-[-0.02em]">{title}</h2>
                  {description && <p className="mt-0.5 text-2xs text-muted">{description}</p>}
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  aria-label="Close"
                  className="-mr-1.5 -mt-1 grid size-9 place-items-center rounded text-muted transition-colors hover:bg-mist/50 hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
                >
                  <svg viewBox="0 0 16 16" className="size-4" fill="currentColor" aria-hidden="true">
                    <path d="M4.3 3.24 8 6.94l3.7-3.7 1.06 1.06-3.7 3.7 3.7 3.7-1.06 1.06-3.7-3.7-3.7 3.7-1.06-1.06 3.7-3.7-3.7-3.7z" />
                  </svg>
                </button>
              </header>
            )}

            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">{children}</div>

            {footer && <div className="border-t border-mist bg-paper px-5 py-4">{footer}</div>}
          </m.div>
        </div>
      )}
    </AnimatePresence>,
    target,
  );
};

export const Dialog = ({ open, onClose, title, description, children, footer, size = 'md' }) => {
  const panelRef = useDismissable(open, onClose);
  const reduced = useReducedMotion();
  const target = portalTarget();

  const widths = { sm: 'max-w-sm', md: 'max-w-lg', lg: 'max-w-2xl' };

  if (!target) return null;

  return createPortal(
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-50 grid place-items-center p-4" role="presentation">
          <m.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            onClick={onClose}
            className="absolute inset-0 bg-ink/35 backdrop-blur-[2px]"
          />

          <m.div
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-label={title}
            tabIndex={-1}
            initial={reduced ? { opacity: 0 } : { opacity: 0, scale: 0.97, y: 8 }}
            animate={reduced ? { opacity: 1 } : { opacity: 1, scale: 1, y: 0 }}
            exit={reduced ? { opacity: 0 } : { opacity: 0, scale: 0.97 }}
            transition={{ duration: reduced ? 0.15 : 0.22, ease: [0.25, 1, 0.5, 1] }}
            className={cn(
              'relative w-full border border-mist bg-surface shadow-[0_20px_60px_-20px_rgba(20,23,26,0.35)] outline-none',
              widths[size],
            )}
          >
            {title && (
              <header className="border-b border-mist px-6 py-5">
                <h2 className="font-display text-lg tracking-[-0.02em]">{title}</h2>
                {description && <p className="mt-1 text-xs text-muted">{description}</p>}
              </header>
            )}
            <div className="px-6 py-5">{children}</div>
            {footer && <div className="flex justify-end gap-2 border-t border-mist bg-paper px-6 py-4">{footer}</div>}
          </m.div>
        </div>
      )}
    </AnimatePresence>,
    target,
  );
};

export default Sheet;
